import { useCallback } from 'react';

import { useApi } from '@paradigm/api';

import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import {
  GrfqInstrument,
  isFutureInstrument,
  isOptionInstrument,
} from '#/unified-rfqs/entities/grfq/domain';
import { exceptionLogger } from '@paradigm/logging/src/logging';
import { GrfqInstruments, INITIAL_INSTRUMENTS_STATE } from './types';

type InstrumentsFetcher = (
  venue: string,
  cb: (instruments: GrfqInstruments) => void,
) => void;

export function useGrfqInstrumentsFetcher(): InstrumentsFetcher {
  const { listInstruments } = useGrfqService();
  const [, fetchInstruments] = useApi(listInstruments);

  const fetchGrfqInstruments: InstrumentsFetcher = useCallback(
    (venue, cb) => {
      const doIterativeFetch = async () => {
        let cursor: string | undefined;
        const instrumentsLists: (readonly GrfqInstrument[])[] = [];

        while (true) {
          const resp = await fetchInstruments({ venue, cursor }).promise;
          if (!resp.ok) {
            cb(INITIAL_INSTRUMENTS_STATE);
            return;
          }
          instrumentsLists.push(resp.data.results);
          if (resp.data.next == null) break;
          cursor = resp.data.next;
        }

        const instruments = instrumentsLists.flat();
        cb({
          futureInstruments: instruments.filter(isFutureInstrument),
          optionInstruments: instruments.filter(isOptionInstrument),
        });
      };
      doIterativeFetch().catch(
        exceptionLogger('Failed fetching grfq instruments'),
      );
    },
    [fetchInstruments],
  );

  return fetchGrfqInstruments;
}
